import { useCallback, useReducer } from 'react';
import { useMutation } from '@apollo/client/react/hooks';
import { gql } from '@yi/core';
import {
  Mutate_ValidateEmail,
  Mutate_ValidateEmailVariables,
} from '@yi/core/src/gql/generated/typesClient/Mutate_ValidateEmail';

import { useEmailSchema } from '~/src/common/schema/useEmailSchema';

export type EmailValidationState = {
  status: 'idle' | 'validating' | 'validated';
  error: string | null;
};
type Action =
  | {
      type: 'validation started';
    }
  | {
      type: 'validation error';
      error: string;
    }
  | {
      type: 'validation success';
    }
  | {
      type: 'email changed';
    };

const initialState: EmailValidationState = {
  status: 'idle',
  error: null,
};
function emailValidationReducer(state: EmailValidationState, action: Action): EmailValidationState {
  switch (action.type) {
    case 'validation started': {
      return {
        ...state,
        status: 'validating',
      };
    }
    case 'validation error': {
      return {
        ...state,
        status: 'validated',
        error: action.error,
      };
    }
    case 'validation success': {
      return {
        ...state,
        status: 'validated',
      };
    }
    case 'email changed': {
      return {
        ...state,
        status: 'idle',
        error: null,
      };
    }
    default: {
      return state;
    }
  }
}
export function useEmailValidation() {
  const [emailValidationState, dispatch] = useReducer(emailValidationReducer, initialState);

  const [serverValidateEmail] = useMutation<Mutate_ValidateEmail, Mutate_ValidateEmailVariables>(
    gql.Mutate_ValidateEmail,
  );

  const emailSchema = useEmailSchema();
  const validateEmail = useCallback(
    (email: string) => {
      dispatch({ type: 'validation started' });
      emailSchema.validate(email).then(
        () => {
          serverValidateEmail({ variables: { email } }).then(result => {
            const error = result?.data?.validateEmail.error;
            if (error) {
              dispatch({ type: 'validation error', error });
            } else {
              dispatch({ type: 'validation success' });
            }
          });
        },
        () => {
          // if basic email validation is failed, we don't need to validate on server
          dispatch({ type: 'validation error', error: '' });
        },
      );
    },
    [emailSchema, serverValidateEmail],
  );

  const onEmailChanged = useCallback(() => {
    dispatch({ type: 'email changed' });
  }, []);

  return { validateEmail, emailValidationState, onEmailChanged };
}
